"use client";

import React, { useEffect, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Cpu, Bot, Trophy, Compass, Sparkles, ChevronRight, Languages } from "lucide-react";
const CONTENT = {
  vi: {
    breadcrumbHome: "Trang chủ",
    breadcrumbCurrent: "Lộ trình Robotics",
    badge: "HỌC VIỆN CÔNG NGHỆ ROBOTO • LỘ TRÌNH CHUẨN",
    heading: "LỘ TRÌNH ROBOTICS",
    subheading: "Từ kỹ sư nhí đến nhà sáng tạo robot",
    paragraph: "ROBOTO xây dựng lộ trình Robotics theo từng giai đoạn phát triển, bắt đầu từ nền tảng lắp ráp và tư duy kỹ thuật, tiến tới lập trình robot, cảm biến, robot di động, robot tự hành và giải quyết nhiệm vụ.",
    primaryCta: "ĐĂNG KÝ TƯ VẤN",
    secondaryCta: "KHÁM PHÁ LỘ TRÌNH",
    switchLang: "English",
    stagesTitle: "3 GIAI ĐOẠN ĐÀO TẠO LIÊN TỤC",
    stage1Name: "Giai đoạn 01 • Nền tảng Robotics",
    stage1Desc: "4–6 tuổi • Duplo Engineering (D1 → D4)",
    stage2Name: "Giai đoạn 02 • Robotics & Lập trình",
    stage2Desc: "7+ tuổi • WhalesBot AI Module 1S (HP1 → HP6)",
    stage3Name: "Giai đoạn 03 • Luyện thi Chuyên sâu",
    stage3Desc: "Sau HP6 • Đấu trường ENJOY AI Quốc tế",
    cardStatus: "HỆ THỐNG GIẢNG DẠY TIÊU CHUẨN",
    stat1Label: "Khóa học cốt lõi",
    stat1Val: "10 Khóa",
    stat2Label: "Bộ kit tiêu chuẩn",
    stat2Val: "2 Nền tảng",
    stat3Label: "Sĩ số nhóm",
    stat3Val: "2–3 Trẻ/Kit"
  },
  en: {
    breadcrumbHome: "Home",
    breadcrumbCurrent: "Robotics Learning Pathway",
    badge: "ROBOTO TECHNOLOGY ACADEMY • OFFICIAL PATHWAY",
    heading: "ROBOTICS LEARNING PATHWAY",
    subheading: "From Junior Engineers to Robot Creators",
    paragraph: "ROBOTO builds a structured Robotics pathway across developmental stages, starting from mechanical assembly and engineering thinking, progressing to robot programming, sensors, mobile robotics, autonomous robotics, and mission problem solving.",
    primaryCta: "BOOK CONSULTATION",
    secondaryCta: "EXPLORE PATHWAY",
    switchLang: "Tiếng Việt",
    stagesTitle: "3 PROGRESSIVE LEARNING PHASES",
    stage1Name: "Stage 01 • Robotics Foundation",
    stage1Desc: "Ages 4–6 • Duplo Engineering (D1 → D4)",
    stage2Name: "Stage 02 • Robotics & Programming",
    stage2Desc: "Ages 7+ • WhalesBot AI Module 1S (HP1 → HP6)",
    stage3Name: "Stage 03 • Advanced Competition",
    stage3Desc: "Post-HP6 • ENJOY AI International Arenas",
    cardStatus: "STANDARDIZED ACADEMY CURRICULUM",
    stat1Label: "Core Courses",
    stat1Val: "10 Modules",
    stat2Label: "Hardware Kits",
    stat2Val: "2 Platforms",
    stat3Label: "Group Ratio",
    stat3Val: "2–3 Kids/Kit"
  }
};
export default function RoboticsHero() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  useEffect(() => {
    try {
      const stored = localStorage.getItem("roboto_lang");
      if (stored === "en" || stored === "vi") {
        setLang(stored);
      }
    } catch {
      // Ignore localStorage access issues
    }
    const handleCustomChange = (e: Event) => {
      const customEvent = e as CustomEvent<{
        lang?: "vi" | "en";
      }>;
      if (customEvent.detail?.lang) {
        setLang(customEvent.detail.lang);
      }
    };
    window.addEventListener("roboto-lang-change", handleCustomChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleCustomChange);
    };
  }, []);
  const toggleLanguage = () => {
    const nextLang = lang === "vi" ? "en" : "vi";
    setLang(nextLang);
    try {
      localStorage.setItem("roboto_lang", nextLang);
      window.dispatchEvent(new CustomEvent("roboto-lang-change", {
        detail: {
          lang: nextLang
        }
      }));
    } catch {
      // Ignore write errors
    }
  };
  const t = CONTENT[lang];
  return <section id="roboticshero" data-nav="light" className="relative overflow-hidden bg-background text-foreground py-16 md:py-24 border-b border-border isolate">
      {/* Background Engineering Grid and Circuit Accents */}
      <div className="pointer-events-none absolute inset-0 -z-10 opacity-40" aria-hidden="true">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--border)_1px,transparent_1px),linear-gradient(to_bottom,var(--border)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />
        <div className="absolute top-0 right-1/4 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute bottom-10 left-10 h-72 w-72 rounded-full bg-secondary/15 blur-2xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-7xl">
        {/* Breadcrumb Navigation */}
        <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs text-muted-foreground mb-4">
          <Link to="/" className="hover:text-foreground transition-colors">
            {t.breadcrumbHome}
          </Link>
          <ChevronRight className="size-3 text-muted-foreground/60" />
          <span className="font-semibold text-foreground">{t.breadcrumbCurrent}</span>
        </nav>

        {/* Top utility bar with quick language toggle */}
        <div className="flex items-center justify-between pb-6 mb-8 border-b border-border/60">
          <Badge variant="outline" className="border-primary/30 text-primary bg-primary/5 font-medium tracking-wide uppercase px-3 py-1 text-xs">
            <Sparkles className="size-3 text-primary" />
            {t.badge}
          </Badge>

          <Button type="button" variant="ghost" size="sm" onClick={toggleLanguage} className="text-xs font-medium text-muted-foreground hover:text-foreground border border-border/60 hover:bg-muted/60" aria-label="Switch language between Vietnamese and English">
            <Languages className="size-3.5 mr-1 text-primary" />
            {t.switchLang}
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Left Column: Heading, Subheading, Narrative Copy, CTAs */}
          <div className="lg:col-span-7 space-y-6">
            <div className="space-y-3">
              <span className="inline-block text-sm font-semibold tracking-wider text-secondary uppercase">
                {t.subheading}
              </span>
              <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground leading-[1.15]">
                {t.heading}
              </h1>
            </div>

            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl font-sans">
              {t.paragraph}
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap items-center gap-4 pt-2">
              <Button asChild size="lg" className="bg-primary text-primary-foreground font-semibold shadow-md hover:bg-primary/95 transition-all px-7">
                <Link to="/FreeTrialContactPage">
                  {t.primaryCta}
                  <ArrowRight className="size-4 ml-1.5" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-border hover:bg-muted/60 font-semibold px-6">
                <Link to="#pathwayoverviewroadmap">
                  <Compass className="size-4 mr-1.5 text-muted-foreground" />
                  {t.secondaryCta}
                </Link>
              </Button>
            </div>

            {/* Quick Metrics Bar */}
            <div className="grid grid-cols-3 gap-4 pt-6 border-t border-border/70 max-w-xl">
              <div>
                <p className="text-2xl font-bold font-serif text-foreground">
                  {t.stat1Val}
                </p>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mt-0.5">
                  {t.stat1Label}
                </p>
              </div>
              <div className="border-l border-border/60 pl-4">
                <p className="text-2xl font-bold font-serif text-foreground">
                  {t.stat2Val}
                </p>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mt-0.5">
                  {t.stat2Label}
                </p>
              </div>
              <div className="border-l border-border/60 pl-4">
                <p className="text-2xl font-bold font-serif text-foreground">
                  {t.stat3Val}
                </p>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mt-0.5">
                  {t.stat3Label}
                </p>
              </div>
            </div>
          </div>

          {/* Right Column: Interactive Academy Pathway Architecture Card */}
          <div className="lg:col-span-5">
            <div className="relative rounded-2xl border border-border bg-card p-6 sm:p-7 shadow-lg space-y-6">
              {/* Card Header */}
              <div className="flex items-center justify-between pb-4 border-b border-border/70">
                <div className="flex items-center gap-2">
                  <div className="size-3 rounded-full bg-primary" />
                  <span className="text-xs font-semibold tracking-wider uppercase text-muted-foreground">
                    {t.cardStatus}
                  </span>
                </div>
                <Badge variant="secondary" className="text-[11px] font-medium">
                  ROBOTO STEM
                </Badge>
              </div>

              {/* Pathway Stages Overview */}
              <div className="space-y-4">
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  {t.stagesTitle}
                </p>

                {/* Stage 1 */}
                <div className="group rounded-xl border border-border/80 bg-background/60 p-4 transition-all hover:border-primary/40 hover:bg-background">
                  <div className="flex items-start gap-3.5">
                    <div className="size-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 text-primary">
                      <Bot className="size-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h2 className="text-sm font-bold text-foreground truncate">
                          {t.stage1Name}
                        </h2>
                        <ChevronRight className="size-4 text-muted-foreground/50 group-hover:text-primary transition-colors" />
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {t.stage1Desc}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Stage 2 */}
                <div className="group rounded-xl border border-border/80 bg-background/60 p-4 transition-all hover:border-secondary/60 hover:bg-background">
                  <div className="flex items-start gap-3.5">
                    <div className="size-9 rounded-lg bg-secondary/15 flex items-center justify-center shrink-0 text-secondary">
                      <Cpu className="size-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h2 className="text-sm font-bold text-foreground truncate">
                          {t.stage2Name}
                        </h2>
                        <ChevronRight className="size-4 text-muted-foreground/50 group-hover:text-secondary transition-colors" />
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {t.stage2Desc}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Stage 3 */}
                <div className="group rounded-xl border border-border/80 bg-background/60 p-4 transition-all hover:border-accent/60 hover:bg-background">
                  <div className="flex items-start gap-3.5">
                    <div className="size-9 rounded-lg bg-accent/15 flex items-center justify-center shrink-0 text-accent">
                      <Trophy className="size-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h2 className="text-sm font-bold text-foreground truncate">
                          {t.stage3Name}
                        </h2>
                        <ChevronRight className="size-4 text-muted-foreground/50 group-hover:text-accent transition-colors" />
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {t.stage3Desc}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Supporting Academy Pathway Visual */}
              <div className="pt-2">
                <div className="relative overflow-hidden rounded-xl border border-border">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_hero_pathway.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_hero_pathway.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_hero_pathway.png-wvc-sizes" alt="ROBOTO Academy Robotics Curriculum Pathway" loading="lazy" decoding="async" className="w-full h-44 object-cover object-center transition-transform duration-500 hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent pointer-events-none" />
                  <div className="absolute bottom-3 left-3 right-3 text-white">
                    <p className="text-xs font-semibold">
                      WhalesBot AI Module 1S &amp; Duplo Engineering
                    </p>
                    <p className="text-[11px] text-white/80">
                      Standardized STEAM Robotics Curriculum
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}